import { useTheme } from "../ThemeContext";
import { useState, useMemo } from "react";
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, AreaChart, Area } from "recharts";
import { fmt, STATUS, DONE_STATUS, PAY_LABELS, PAY_COLORS } from "../theme";
import { KpiCard, DataTable, Card, EmptyState, ChartTooltip } from "../components/UI";
import { exportExcel, exportPDF, fmtMoeda, fmtData, getTurno, filtrarPorPeriodo } from "../utils/exportUtils";

const PERIODOS = [
  { key: "hoje", label: "Hoje" },
  { key: "ontem", label: "Ontem" },
  { key: "semana", label: "Semana" },
  { key: "mes", label: "Mes" },
  { key: "ano", label: "Ano" },
  { key: "custom", label: "Personalizado" },
];
const TURNOS = [
  { key: "Manha", label: "Manha", icon: "🌅", color: "#F59E0B" },
  { key: "Tarde", label: "Tarde", icon: "☀️", color: "#3B82F6" },
  { key: "Noite", label: "Noite", icon: "🌙", color: "#8B5CF6" },
];

export default function RelFinanceiro({ orders, sub, lang }) {
  const { theme: C } = useTheme();
  const [periodo, setPeriodo] = useState("mes");
  const [dataInicio, setDataInicio] = useState("");
  const [dataFim, setDataFim] = useState("");

  const filtrados = useMemo(() => filtrarPorPeriodo(orders, periodo, dataInicio, dataFim), [orders, periodo, dataInicio, dataFim]);
  const vendidos = filtrados.filter(o => DONE_STATUS.includes(o.status));

  // ── Dados financeiros ──
  const totalFat = vendidos.reduce((s, o) => s + Number(o.total || 0), 0);
  const totalSub = vendidos.reduce((s, o) => s + Number(o.subtotal || 0), 0);
  const totalTaxa = vendidos.reduce((s, o) => s + Number(o.taxa_entrega || 0), 0);
  const totalDesc = vendidos.reduce((s, o) => s + Number(o.desconto || 0), 0);
  const ticketMedio = vendidos.length > 0 ? totalFat / vendidos.length : 0;

  // ── Por forma de pagamento ──
  const porPag = useMemo(() => {
    const m = {};
    vendidos.forEach(o => {
      const k = o.forma_pagamento || "outro";
      if (!m[k]) m[k] = { tipo: PAY_LABELS[k] || k, key: k, ped: 0, fat: 0 };
      m[k].ped++;
      m[k].fat += Number(o.total || 0);
    });
    return Object.values(m).sort((a, b) => b.fat - a.fat);
  }, [vendidos]);

  const pgPie = porPag.map(p => ({ name: p.tipo, value: p.fat, color: PAY_COLORS[p.key] || "#888" }));

  // ── Faturamento por dia ──
  const fatDia = useMemo(() => {
    const m = {};
    vendidos.forEach(o => { const d = o.criado_em?.slice(0, 10); if (d) m[d] = (m[d] || 0) + Number(o.total || 0); });
    return Object.entries(m).sort(([a], [b]) => a.localeCompare(b)).map(([d, v]) => ({ dia: d.slice(5), valor: Math.round(v * 100) / 100 }));
  }, [vendidos]);

  // ── Por turno ──
  const turnoData = useMemo(() => {
    const m = { Manha: { fat: 0, ped: 0, taxa: 0 }, Tarde: { fat: 0, ped: 0, taxa: 0 }, Noite: { fat: 0, ped: 0, taxa: 0 } };
    vendidos.forEach(o => {
      const t = getTurno(o.criado_em);
      if (m[t]) { m[t].fat += Number(o.total || 0); m[t].ped++; m[t].taxa += Number(o.taxa_entrega || 0); }
    });
    return m;
  }, [vendidos]);

  const iS = { padding: "7px 12px", borderRadius: 8, border: `1px solid ${C.border}`, background: C.card, color: C.text, fontSize: 12, outline: "none" };

  // ── Exportações ──
  const handleExportExcel = () => {
    const headers = ["Codigo", "Status", "Pagamento", "Subtotal", "Taxa Entrega", "Desconto", "Total", "Turno", "Data"];
    const rows = vendidos.map(o => [
      o.codigo, STATUS[o.status]?.label || o.status, PAY_LABELS[o.forma_pagamento] || o.forma_pagamento,
      Number(o.subtotal || 0).toFixed(2), Number(o.taxa_entrega || 0).toFixed(2), Number(o.desconto || 0).toFixed(2),
      Number(o.total || 0).toFixed(2), getTurno(o.criado_em), fmtData(o.criado_em)
    ]);
    // Linha de totais
    rows.push(["", "", "TOTAIS", totalSub.toFixed(2), totalTaxa.toFixed(2), totalDesc.toFixed(2), totalFat.toFixed(2), "", ""]);
    exportExcel(headers, rows, "relatorio_financeiro");
  };

  const handleExportPDF = () => {
    const kpiHTML = `<div class="kpi-row">
      <div class="kpi"><div class="kpi-val green">${fmtMoeda(totalFat)}</div><div class="kpi-label">Faturamento</div></div>
      <div class="kpi"><div class="kpi-val blue">${fmtMoeda(totalSub)}</div><div class="kpi-label">Subtotal Produtos</div></div>
      <div class="kpi"><div class="kpi-val orange">${fmtMoeda(totalTaxa)}</div><div class="kpi-label">Taxas Entrega</div></div>
      <div class="kpi"><div class="kpi-val">${fmtMoeda(ticketMedio)}</div><div class="kpi-label">Ticket Medio</div></div>
    </div>`;

    const pagHTML = `<h2>Por Forma de Pagamento</h2><table>
      <tr><th>Forma</th><th>Pedidos</th><th>Faturamento</th><th>%</th></tr>
      ${porPag.map(p => `<tr><td>${p.tipo}</td><td>${p.ped}</td><td>${fmtMoeda(p.fat)}</td><td>${totalFat > 0 ? (p.fat / totalFat * 100).toFixed(1) : 0}%</td></tr>`).join("")}
    </table>`;

    const turnoHTML = `<h2>Por Turno</h2><table>
      <tr><th>Turno</th><th>Pedidos</th><th>Faturamento</th><th>Taxas</th></tr>
      ${TURNOS.map(t => `<tr><td>${t.icon} ${t.label}</td><td>${turnoData[t.key].ped}</td><td>${fmtMoeda(turnoData[t.key].fat)}</td><td>${fmtMoeda(turnoData[t.key].taxa)}</td></tr>`).join("")}
    </table>`;

    const detHTML = `<h2>Detalhamento (${vendidos.length} pedidos)</h2><table>
      <tr><th>Codigo</th><th>Pagamento</th><th>Subtotal</th><th>Taxa</th><th>Total</th><th>Data</th></tr>
      ${vendidos.slice(0, 100).map(o => `<tr><td>#${o.codigo}</td><td>${PAY_LABELS[o.forma_pagamento] || "—"}</td><td>${fmtMoeda(o.subtotal)}</td><td>${fmtMoeda(o.taxa_entrega)}</td><td><strong>${fmtMoeda(o.total)}</strong></td><td>${fmtData(o.criado_em)}</td></tr>`).join("")}
    </table>`;

    exportPDF("Relatorio Financeiro", kpiHTML + pagHTML + turnoHTML + detHTML, "relatorio_financeiro");
  };

  const FilterBar = () => (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", marginBottom: 16, padding: 12, background: C.card, borderRadius: 12, border: `1px solid ${C.border}` }}>
      <span style={{ fontSize: 11, fontWeight: 700, color: C.textMuted, marginRight: 4 }}>Periodo:</span>
      {PERIODOS.map(p => (
        <button key={p.key} onClick={() => setPeriodo(p.key)}
          style={{ padding: "5px 12px", borderRadius: 8, border: `1px solid ${periodo === p.key ? C.green : C.border}`,
            background: periodo === p.key ? C.green + "18" : "transparent", color: periodo === p.key ? C.green : C.textMuted,
            cursor: "pointer", fontSize: 11, fontWeight: 700 }}>
          {p.label}
        </button>
      ))}
      {periodo === "custom" && (
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <input type="date" value={dataInicio} onChange={e => setDataInicio(e.target.value)} style={iS} />
          <span style={{ color: C.textMuted, fontSize: 11 }}>ate</span>
          <input type="date" value={dataFim} onChange={e => setDataFim(e.target.value)} style={iS} />
        </div>
      )}
      <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
        <button onClick={handleExportExcel} style={{ padding: "5px 12px", borderRadius: 8, border: `1px solid ${C.green}40`, background: `${C.green}12`, color: C.green, cursor: "pointer", fontSize: 11, fontWeight: 700 }}>Excel</button>
        <button onClick={handleExportPDF} style={{ padding: "5px 12px", borderRadius: 8, border: `1px solid ${C.red}40`, background: `${C.red}12`, color: C.red, cursor: "pointer", fontSize: 11, fontWeight: 700 }}>PDF</button>
      </div>
    </div>
  );

  // ═══ SUB: GERAL ═══
  if (sub === "geral") {
    return (<>
      <FilterBar />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 10, marginBottom: 16 }}>
        <KpiCard label="Faturamento" value={fmt(totalFat)} color={C.green} delay={0} />
        <KpiCard label="Subtotal Produtos" value={fmt(totalSub)} color={C.blue} delay={.05} />
        <KpiCard label="Taxas Entrega" value={fmt(totalTaxa)} color={C.orange} delay={.1} />
        <KpiCard label="Descontos" value={fmt(totalDesc)} color={C.red} delay={.15} />
        <KpiCard label="Ticket Medio" value={fmt(ticketMedio)} color={C.purple || "#8B5CF6"} delay={.2} />
      </div>

      {/* Turnos financeiros */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10, marginBottom: 16 }}>
        {TURNOS.map(t => (
          <div key={t.key} style={{ background: C.card, borderRadius: 12, padding: 14, border: `1px solid ${C.border}`, borderLeft: `4px solid ${t.color}` }}>
            <div style={{ fontSize: 10, color: C.textMuted, marginBottom: 6 }}>{t.icon} {t.label}</div>
            <div style={{ fontSize: 18, fontWeight: 800, color: C.green }}>{fmt(turnoData[t.key]?.fat || 0)}</div>
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6 }}>
              <span style={{ fontSize: 10, color: C.textMuted }}>{turnoData[t.key]?.ped || 0} pedidos</span>
              <span style={{ fontSize: 10, color: C.orange }}>Taxa: {fmt(turnoData[t.key]?.taxa || 0)}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Graficos */}
      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 12, marginBottom: 16 }}>
        <Card>
          <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>Faturamento por Dia</h3>
          {fatDia.length > 0 ? (
            <ResponsiveContainer width="100%" height={180}>
              <AreaChart data={fatDia}>
                <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
                <XAxis dataKey="dia" tick={{ fill: C.textMuted, fontSize: 9 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: C.textMuted, fontSize: 9 }} axisLine={false} tickLine={false} />
                <Tooltip content={<ChartTooltip />} />
                <Area type="monotone" dataKey="valor" stroke={C.green} fill={C.green + "30"} strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          ) : <EmptyState text="Dados insuficientes" />}
        </Card>

        <Card>
          <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>Forma de Pagamento</h3>
          {pgPie.length > 0 ? (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
              <PieChart width={150} height={150}>
                <Pie data={pgPie} cx={72} cy={72} innerRadius={40} outerRadius={65} paddingAngle={3} dataKey="value">
                  {pgPie.map((e, i) => <Cell key={i} fill={e.color} />)}
                </Pie>
              </PieChart>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, justifyContent: "center", marginTop: 8 }}>
                {pgPie.map((p, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                    <div style={{ width: 8, height: 8, borderRadius: 2, background: p.color }} />
                    <span style={{ fontSize: 9, color: C.textSoft }}>{p.name}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : <EmptyState />}
        </Card>
      </div>

      {/* Tabela detalhada */}
      <Card>
        <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>Detalhamento por Forma de Pagamento</h3>
        {porPag.length === 0 ? <EmptyState /> : (
          <DataTable headers={["Forma", "Pedidos", "Faturamento", "Ticket Medio", "%"]}>
            {porPag.map((p, i) => (
              <tr key={i}>
                <td style={{ padding: "10px 12px", fontWeight: 700, fontSize: 13 }}>
                  <span style={{ display: "inline-block", width: 10, height: 10, borderRadius: 3, background: PAY_COLORS[p.key] || "#888", marginRight: 8 }} />
                  {p.tipo}
                </td>
                <td style={{ padding: "10px 12px", fontWeight: 700 }}>{p.ped}</td>
                <td style={{ padding: "10px 12px", fontWeight: 800, color: C.green }}>{fmt(p.fat)}</td>
                <td style={{ padding: "10px 12px", color: C.textSoft }}>{fmt(p.ped > 0 ? p.fat / p.ped : 0)}</td>
                <td style={{ padding: "10px 12px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <div style={{ flex: 1, height: 6, borderRadius: 3, background: C.border }}>
                      <div style={{ width: `${totalFat > 0 ? (p.fat / totalFat * 100) : 0}%`, height: "100%", borderRadius: 3, background: PAY_COLORS[p.key] || "#888" }} />
                    </div>
                    <span style={{ fontSize: 11, fontWeight: 700, color: C.textSoft, minWidth: 36 }}>{totalFat > 0 ? (p.fat / totalFat * 100).toFixed(1) : 0}%</span>
                  </div>
                </td>
              </tr>
            ))}
            {/* Linha de totais */}
            <tr style={{ borderTop: `2px solid ${C.border}` }}>
              <td style={{ padding: "10px 12px", fontWeight: 800, fontSize: 13 }}>TOTAL</td>
              <td style={{ padding: "10px 12px", fontWeight: 800 }}>{vendidos.length}</td>
              <td style={{ padding: "10px 12px", fontWeight: 800, color: C.green, fontSize: 15 }}>{fmt(totalFat)}</td>
              <td style={{ padding: "10px 12px", fontWeight: 700, color: C.textSoft }}>{fmt(ticketMedio)}</td>
              <td style={{ padding: "10px 12px", fontWeight: 800 }}>100%</td>
            </tr>
          </DataTable>
        )}
      </Card>
    </>);
  }

  // ═══ SUB: Por tipo de pagamento ═══
  if (["dinheiro", "credito", "pix", "cashback", "debito"].includes(sub)) {
    const label = PAY_LABELS[sub] || sub;
    const lista = vendidos.filter(o => o.forma_pagamento === sub);
    const fat = lista.reduce((s, o) => s + Number(o.total || 0), 0);
    const avg = lista.length > 0 ? fat / lista.length : 0;

    const porDia = {};
    lista.forEach(o => { const d = o.criado_em?.slice(0, 10); if (d) porDia[d] = (porDia[d] || 0) + Number(o.total || 0); });
    const diaChart = Object.entries(porDia).sort(([a], [b]) => a.localeCompare(b)).map(([d, v]) => ({ dia: d.slice(5), valor: Math.round(v * 100) / 100 }));

    return (<>
      <FilterBar />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 16 }}>
        <KpiCard label={`Pedidos ${label}`} value={lista.length} color={C.blue} delay={0} />
        <KpiCard label="Faturamento" value={fmt(fat)} color={C.green} delay={.06} />
        <KpiCard label="Ticket Medio" value={fmt(avg)} color={C.orange} delay={.12} />
        <KpiCard label="% do Total" value={totalFat > 0 ? `${(fat / totalFat * 100).toFixed(1)}%` : "0%"} color={C.purple || "#8B5CF6"} delay={.18} />
      </div>

      {diaChart.length > 1 && (
        <Card style={{ marginBottom: 16 }}>
          <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>{label} por Dia</h3>
          <ResponsiveContainer width="100%" height={160}>
            <BarChart data={diaChart}>
              <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
              <XAxis dataKey="dia" tick={{ fill: C.textMuted, fontSize: 9 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: C.textMuted, fontSize: 9 }} axisLine={false} tickLine={false} />
              <Tooltip content={<ChartTooltip />} />
              <Bar dataKey="valor" radius={[4, 4, 0, 0]} fill={PAY_COLORS[sub] || C.blue} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      )}

      <Card>
        {lista.length === 0 ? <EmptyState text={`Nenhum pedido com ${label} no periodo`} /> : (
          <DataTable headers={["Codigo", "Status", "Total", "Turno", "Data"]}>
            {lista.slice(0, 50).map(p => (
              <tr key={p.id}>
                <td style={{ padding: "10px 12px", fontWeight: 700, fontSize: 12 }}>#{p.codigo}</td>
                <td style={{ padding: "10px 12px" }}>
                  <span style={{ fontSize: 10, fontWeight: 700, color: STATUS[p.status]?.color, background: STATUS[p.status]?.bg, padding: "3px 8px", borderRadius: 6 }}>{STATUS[p.status]?.label || p.status}</span>
                </td>
                <td style={{ padding: "10px 12px", fontWeight: 800, color: C.green }}>{fmt(p.total)}</td>
                <td style={{ padding: "10px 12px", fontSize: 10, color: C.textMuted }}>{getTurno(p.criado_em)}</td>
                <td style={{ padding: "10px 12px", fontSize: 11, color: C.textMuted }}>{fmtData(p.criado_em)}</td>
              </tr>
            ))}
          </DataTable>
        )}
      </Card>
    </>);
  }

  return <EmptyState text="Selecione uma opcao" />;
}
